import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Match,
  MatchFilterOptions,
  MatchFilters,
  MatchListResponse,
  MatchStatut,
  MatchSuggestionListResponse,
} from '../models';

export interface MatchListParams extends MatchFilters {
  page?: number;
  limit?: number;
}

@Injectable({ providedIn: 'root' })
export class MatchApiService {
  private http = inject(HttpClient);
  private matchesBase = `${environment.apiUrl}/matches`;
  private matchingsBase = `${environment.apiUrl}/matchings`;

  /** Public-profiles flow — unchanged. */
  create(payload: {
    studentId: string;
    offreId: string;
    companyId?: string;
  }): Observable<unknown> {
    return this.http.post(this.matchesBase, payload);
  }

  list(params: MatchListParams = {}): Observable<MatchListResponse> {
    const page = params.page ?? 0;
    const limit = params.limit ?? 10;
    let httpParams = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }
    if (params.dateFrom) {
      httpParams = httpParams.set('dateFrom', params.dateFrom);
    }
    if (params.dateTo) {
      httpParams = httpParams.set('dateTo', params.dateTo);
    }
    if (params.statut) {
      httpParams = httpParams.set('statut', params.statut);
    }
    if (params.typeMatching) {
      httpParams = httpParams.set('typeMatching', params.typeMatching);
    }
    if (params.entrepriseId) {
      httpParams = httpParams.set('entrepriseId', params.entrepriseId);
    }
    if (params.apprenantId) {
      httpParams = httpParams.set('apprenantId', params.apprenantId);
    }
    if (params.conseillerIds?.length) {
      httpParams = httpParams.set('conseillerIds', params.conseillerIds.join(','));
    } else if (params.conseillerId) {
      httpParams = httpParams.set('conseillerId', params.conseillerId);
    }
    if (params.offreId) {
      httpParams = httpParams.set('offreId', params.offreId);
    }
    if (params.scoreMin != null) {
      httpParams = httpParams.set('scoreMin', String(params.scoreMin));
    }
    if (params.avecContrat) {
      httpParams = httpParams.set('avecContrat', 'true');
    }

    return this.http.get<MatchListResponse>(this.matchingsBase, {
      params: httpParams,
    });
  }

  getById(id: string): Observable<Match> {
    return this.http.get<Match>(
      `${this.matchingsBase}/${encodeURIComponent(id)}`,
    );
  }

  filterOptions(): Observable<MatchFilterOptions> {
    return this.http.get<MatchFilterOptions>(
      `${this.matchingsBase}/filters/options`,
    );
  }

  updateStatut(
    id: string,
    statut: MatchStatut,
    remarque?: string,
  ): Observable<Match> {
    return this.http.patch<Match>(
      `${this.matchingsBase}/${encodeURIComponent(id)}/statut`,
      { statut, remarque },
    );
  }

  remove(id: string): Observable<unknown> {
    return this.http.delete(
      `${this.matchingsBase}/${encodeURIComponent(id)}`,
    );
  }

  listSuggestions(
    params: MatchListParams = {},
  ): Observable<MatchSuggestionListResponse> {
    const page = params.page ?? 0;
    const limit = params.limit ?? 10;
    let httpParams = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }
    if (params.entrepriseId) {
      httpParams = httpParams.set('entrepriseId', params.entrepriseId);
    }
    if (params.apprenantId) {
      httpParams = httpParams.set('apprenantId', params.apprenantId);
    }
    if (params.offreId) {
      httpParams = httpParams.set('offreId', params.offreId);
    }
    if (params.scoreMin != null) {
      httpParams = httpParams.set('scoreMin', String(params.scoreMin));
    }

    return this.http.get<MatchSuggestionListResponse>(
      `${this.matchingsBase}/suggestions`,
      { params: httpParams },
    );
  }

  acceptSuggestion(id: string): Observable<{ matchingId: string }> {
    return this.http.post<{ matchingId: string }>(
      `${this.matchingsBase}/suggestions/${encodeURIComponent(id)}/accepter`,
      {},
    );
  }

  dismissSuggestion(id: string): Observable<unknown> {
    return this.http.post(
      `${this.matchingsBase}/suggestions/${encodeURIComponent(id)}/ignorer`,
      {},
    );
  }
}
